import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { analytics, functionsInstance } from '../../firebase/firebase';
import { httpsCallable } from 'firebase/functions';
import TrialPromptPopup from './TrialPromptPopup';

const HOLIDAY_DATE = new Date('2026-05-05');
const HOLIDAY_TAG  = 'cinco-de-mayo-2026';

function getDaysAway() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((HOLIDAY_DATE - today) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "is TODAY! 🎉";
  if (diff === 1) return "is TOMORROW ⏰";
  if (diff < 0)  return "has passed — but it's never too early for next year!";
  return `is in ${diff} days ⏰`;
}

const CincoDeMayo2026 = () => {
  const hasLoggedRead = useRef(false);
  const [email, setEmail]               = useState('');
  const [gdprChecked, setGdprChecked]   = useState(false);
  const [submitState, setSubmitState]   = useState('idle');
  const [errorMsg, setErrorMsg]         = useState('');
  const [showTrialPopup, setShowTrialPopup] = useState(false);

  const daysAway = getDaysAway();

  useEffect(() => {
    document.title = "Best TPT Keywords for Cinco de Mayo 2026 | Mr Joel's Seller Tools";
    window.scrollTo(0, 0);
    logEvent(analytics, 'page_view', {
      page_title: 'Cinco de Mayo Keyword Report Signup',
      page_path: '/keywords/cinco-de-mayo-2026'
    });
    const handleScroll = () => {
      if (window.scrollY > 300 && !hasLoggedRead.current) {
        logEvent(analytics, 'cinco_de_mayo_read_content');
        hasLoggedRead.current = true;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!gdprChecked) return;
    setSubmitState('loading');
    setErrorMsg('');
    try {
      logEvent(analytics, 'cinco_de_mayo_signup_submit');
      const subscribeToReport = httpsCallable(functionsInstance, 'subscribeToSeasonalReport');
      const result = await subscribeToReport({ email, tag: HOLIDAY_TAG });
      const { userType } = result.data;
      if (userType === 'existing') {
        setSubmitState('success-existing');
        logEvent(analytics, 'cinco_de_mayo_existing_user');
      } else {
        setSubmitState('success-new');
        logEvent(analytics, 'cinco_de_mayo_new_subscriber');
        setTimeout(() => setShowTrialPopup(true), 5000);
      }
    } catch (err) {
      console.error('Signup error:', err);
      setErrorMsg('Something went wrong — please try again or email info@mrjoelstptgenerator.com');
      setSubmitState('error');
    }
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    const input = document.getElementById('cdm-email');
    if (input) { input.focus(); input.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
  };

  // Top 20 keywords — scores capped at 100
  const KEYWORDS = [
    { rank: 1,  kw: 'cinco de mayo',              note: '🔥 Dominant search term — massive seasonal demand',   score: 100, pop: '2,096',  comp: '18,384', tier: 'gold',   capped: true  },
    { rank: 2,  kw: 'cinco de mayo craft',         note: '💎 Best opportunity — very low competition',          score: 100, pop: '682',   comp: '2,256',  tier: 'gold',   capped: true  },
    { rank: 3,  kw: 'cinco de mayo activities',    note: 'Broad activity bundles performing well',               score: 92,  pop: '386',   comp: '15,358', tier: 'gold',   capped: false },
    { rank: 4,  kw: 'cinco de mayo preschool',     note: '🎒 Grade specific — low competition sweet spot',       score: 46,  pop: '171',   comp: '5,668',  tier: 'strong', capped: false },
    { rank: 5,  kw: 'cinco de mayo math',          note: '➕ Subject specific — low competition ✅',             score: 35,  pop: '125',   comp: '4,153',  tier: 'strong', capped: false },
    { rank: 6,  kw: 'cinco de mayo clipart',       note: '🖼️ Very low competition — easy to rank ✅',            score: 24,  pop: '74',    comp: '1,188',  tier: 'strong', capped: false },
    { rank: 7,  kw: 'cinco de mayo coloring pages',note: '🎨 Low competition coloring niche ✅',                  score: 21,  pop: '76',    comp: '3,755',  tier: 'strong', capped: false },
    { rank: 8,  kw: 'cinco de mayo word search',   note: '🔎 Very low competition — quick to produce ✅',         score: 16,  pop: '49',    comp: '1,113',  tier: 'moderate', capped: false },
    { rank: 9,  kw: 'cinco de mayo bulletin board',note: '📌 Low competition display niche ✅',                   score: 16,  pop: '55',    comp: '3,252',  tier: 'moderate', capped: false },
    { rank: 10, kw: 'cinco de mayo reading',       note: '📖 Subject + holiday combo',                           score: 13,  pop: '47',    comp: '4,695',  tier: 'moderate', capped: false },
    // Hidden gems — searched but almost no products
    { rank: 11, kw: 'cinco de mayo worksheets',    note: '💎 Searched but barely any products exist yet',        score: null, pop: '—',    comp: 'Minimal ✅', tier: 'gem', capped: false },
    { rank: 12, kw: 'cinco de mayo kindergarten',  note: '💎 Grade specific — first mover opportunity',          score: null, pop: '—',    comp: 'Minimal ✅', tier: 'gem', capped: false },
    { rank: 13, kw: 'cinco de mayo spanish',       note: '💎 Bilingual niche — very underserved',                score: null, pop: '—',    comp: 'Minimal ✅', tier: 'gem', capped: false },
    { rank: 14, kw: 'cinco de mayo lesson plans',  note: '💎 High value format — barely any products',           score: null, pop: '—',    comp: 'Minimal ✅', tier: 'gem', capped: false },
    { rank: 15, kw: 'cinco de mayo social studies activities 3rd grade', note: '💎 Grade + subject combo — zero competition', score: null, pop: '—', comp: 'Zero ✅', tier: 'gem', capped: false },
    { rank: 16, kw: 'cinco de mayo social studies informational text',   note: '💎 Reading + social studies niche',           score: null, pop: '—', comp: 'Minimal ✅', tier: 'gem', capped: false },
    { rank: 17, kw: 'cinco de mayo informational text reading passages', note: '💎 Literacy niche — very underserved',         score: null, pop: '—', comp: 'Minimal ✅', tier: 'gem', capped: false },
    { rank: 18, kw: 'cinco de mayo history for kids printable activities', note: '💎 History niche — barely any products',    score: null, pop: '—', comp: 'Minimal ✅', tier: 'gem', capped: false },
    { rank: 19, kw: 'cinco de mayo reading activities informational text', note: '💎 Reading + activity combo',               score: null, pop: '—', comp: 'Minimal ✅', tier: 'gem', capped: false },
    { rank: 20, kw: 'cinco de mayo history and culture activities',      note: '💎 Cultural education niche',                  score: null, pop: '—', comp: 'Minimal ✅', tier: 'gem', capped: false },
  ];

  const visibleRows  = KEYWORDS.slice(0, 5);
  const blurredRows  = KEYWORDS.slice(5, 8);

  const getScoreBadge = (kw) => {
    if (kw.tier === 'gem') return (
      <span style={{background: '#92400e', color: '#fbbf24', fontSize: '11px', fontWeight: 800, padding: '4px 10px', borderRadius: '20px', whiteSpace: 'nowrap'}}>💎 GEM</span>
    );
    const bg    = kw.tier === 'gold' ? '#f0fdf4' : kw.tier === 'strong' ? '#eff6ff' : 'white';
    const border= kw.tier === 'gold' ? '#16a34a' : kw.tier === 'strong' ? '#1a56db' : '#e2e8f0';
    const color = kw.tier === 'gold' ? '#15803d' : kw.tier === 'strong' ? '#1d4ed8' : '#64748b';
    return (
      <span style={{background: bg, border: `1px solid ${border}`, color, fontSize: '13px', fontWeight: 800, padding: '4px 12px', borderRadius: '20px', whiteSpace: 'nowrap', fontFamily: 'Sora, sans-serif'}}>
        ★ {kw.score}{kw.capped ? '*' : ''}
      </span>
    );
  };

  return (
    <div className="cdm-wrapper">

      {showTrialPopup && (
        <TrialPromptPopup
          onClose={() => setShowTrialPopup(false)}
          accentColor="#16a34a"
          accentDark="#14532d"
          accentLight="#f0fdf4"
          accentBorder="#bbf7d0"
        />
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800;900&family=DM+Sans:ital,wght@0,400;0,500;1,400&display=swap');

        .cdm-wrapper {
          --green:       #16a34a;
          --green-dark:  #14532d;
          --green-light: #f0fdf4;
          --green-border:#bbf7d0;
          --red:         #dc2626;
          --red-light:   #fef2f2;
          --blue:        #1a56db;
          --blue-dark:   #1e40af;
          --blue-light:  #eff6ff;
          --gold:        #fbbf24;
          --gold-bg:     #fffbeb;
          --text:        #0f172a;
          --muted:       #64748b;
          --border:      #e2e8f0;
          --bg:          #f8fafc;
          font-family: 'DM Sans', sans-serif;
          background: var(--bg);
          color: var(--text);
          min-height: 100vh;
          overflow-x: hidden;
        }

        .cdm-nav {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 32px; background: white;
          border-bottom: 1px solid var(--border);
          position: sticky; top: 0; z-index: 100;
        }
        .cdm-nav-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
        .cdm-nav-mark {
          width: 36px; height: 36px;
          background: linear-gradient(135deg, var(--green), var(--green-dark));
          border-radius: 9px; display: flex; align-items: center; justify-content: center;
          font-family: 'Sora', sans-serif; font-weight: 900; font-size: 14px; color: white;
        }
        .cdm-nav-text { font-family: 'Sora', sans-serif; font-weight: 700; font-size: 14px; color: var(--text); }
        .cdm-nav-sub  { font-size: 11px; color: var(--muted); }
        .cdm-nav-cta  { font-family: 'Sora', sans-serif; font-size: 13px; font-weight: 600; color: var(--green); text-decoration: none; border: 1px solid var(--green-border); padding: 7px 16px; border-radius: 8px; transition: background 0.15s; }
        .cdm-nav-cta:hover { background: var(--green-light); }

        .cdm-urgency { background: linear-gradient(135deg, var(--green-dark), var(--green)); color: white; text-align: center; padding: 10px 24px; font-size: 14px; font-weight: 600; }

        .cdm-hero { max-width: 1080px; margin: 0 auto; padding: 60px 24px 40px; text-align: center; }
        .cdm-badge { display: inline-flex; align-items: center; gap: 7px; background: var(--green-light); border: 1px solid var(--green-border); color: var(--green-dark); font-size: 12px; font-weight: 700; padding: 6px 16px; border-radius: 20px; margin-bottom: 24px; letter-spacing: 0.03em; text-transform: uppercase; }
        .cdm-badge-dot { width: 6px; height: 6px; background: var(--green); border-radius: 50%; animation: pulse 2s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(1.4); } }

        .cdm-wrapper h1, .sora { font-family: 'Sora', sans-serif; }
        .cdm-title { font-weight: 900; font-size: clamp(30px, 5vw, 54px); line-height: 1.1; letter-spacing: -0.03em; color: var(--text); margin-bottom: 20px; }
        .cdm-title .green-accent { background: linear-gradient(135deg, var(--green), var(--green-dark)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .cdm-title .gold { color: #d97706; -webkit-text-fill-color: #d97706; }
        .cdm-sub { font-size: 18px; color: var(--muted); line-height: 1.7; margin-bottom: 40px; max-width: 800px; margin-left: auto; margin-right: auto; }
        .cdm-sub strong { color: var(--text); }

        .cdm-grid { max-width: 1080px; margin: 0 auto; padding: 0 24px 60px; display: grid; grid-template-columns: 1fr 420px; gap: 40px; align-items: stretch; }
        .cdm-info-card, .cdm-form-card { background: white; border: 1px solid var(--border); border-radius: 24px; padding: 32px; box-shadow: 0 10px 40px rgba(0,0,0,0.06); box-sizing: border-box; }
        .cdm-form-card { position: sticky; top: 100px; }
        .cdm-section-label { font-size: 11px; font-weight: 700; color: var(--green); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 12px; }
        .cdm-card-heading { font-weight: 800; font-size: 22px; color: var(--text); margin-bottom: 24px; line-height: 1.2; }

        .cdm-perks-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .cdm-perk { display: flex; align-items: center; gap: 10px; background: var(--bg); border: 1px solid var(--border); border-radius: 12px; padding: 14px 16px; font-size: 13px; font-weight: 500; color: var(--text); }

        .cdm-field-label { display: block; font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px; }
        .cdm-field-input { width: 100%; padding: 14px; border: 1.5px solid var(--border); border-radius: 12px; font-size: 15px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; margin-bottom: 20px; background: #f8fafc; transition: all 0.2s; box-sizing: border-box; }
        .cdm-field-input:focus { border-color: var(--green); background: white; }
        .cdm-gdpr-box { background: #f8fafc; border: 1px solid var(--border); border-radius: 12px; padding: 14px; margin-bottom: 20px; }
        .cdm-gdpr-label { display: flex; gap: 10px; cursor: pointer; }
        .cdm-gdpr-checkbox { width: 16px; height: 16px; flex-shrink: 0; margin-top: 3px; accent-color: var(--green); }
        .cdm-gdpr-text { font-size: 12px; color: var(--muted); line-height: 1.5; }
        .cdm-gdpr-text a { color: var(--green); }
        .cdm-submit-btn { width: 100%; background: linear-gradient(135deg, var(--green), var(--green-dark)); color: white; font-weight: 800; font-size: 15px; padding: 16px; border: none; border-radius: 12px; cursor: pointer; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; font-family: 'Sora', sans-serif; }
        .cdm-submit-btn:hover { transform: translateY(-1px); }
        .cdm-amazon-badge { background: var(--gold-bg); border: 1px solid #fde68a; color: #92400e; font-size: 12px; padding: 12px; border-radius: 10px; margin-top: 16px; text-align: center; }

        .cdm-preview-row { display: grid; grid-template-columns: 32px 1fr 100px 90px 110px; gap: 0; padding: 13px 32px; border-bottom: 1px solid #f1f5f9; align-items: center; }
        .cdm-app-promo { max-width: 1080px; margin: 0 auto; padding: 0 24px 40px; }

        @media (max-width: 850px) {
          .cdm-grid { grid-template-columns: 1fr; }
          .cdm-form-card { position: static; order: -1; }
          .cdm-perks-grid { grid-template-columns: 1fr; }
          .cdm-title { font-size: 30px; }
          .cdm-preview-row { grid-template-columns: 24px 1fr 80px; padding: 12px 16px; }
          .cdm-hide-mobile { display: none; }
          .cdm-nav { padding: 12px 16px; }
        }
      `}</style>

      {/* URGENCY BANNER */}
      <div className="cdm-urgency">
        🌮 Cinco de Mayo {daysAway} — Analysed April 26th 2026 · Data from 75,000+ TPT searches
      </div>

      {/* NAV */}
      <nav className="cdm-nav">
        <Link to="/" className="cdm-nav-logo">
          <div className="cdm-nav-mark">MJ</div>
          <div>
            <div className="cdm-nav-text">Mr Joel's Seller Tools</div>
            <div className="cdm-nav-sub">TPT Keyword Intelligence</div>
          </div>
        </Link>
        <Link to="/app" className="cdm-nav-cta">Try the App Free →</Link>
      </nav>

      {/* HERO */}
      <div className="cdm-hero">
        <div className="cdm-badge">
          <div className="cdm-badge-dot"></div>
          Analysed April 26th 2026 · Free Keyword Report
        </div>
        <h1 className="cdm-title sora">
          Best TPT Keywords for<br />
          <span className="green-accent">Cinco de Mayo 2026</span><br />
          <span className="gold">— Ranked by Opportunity</span>
        </h1>
        <p className="cdm-sub">
          Cinco de Mayo {daysAway}. We just analysed <strong>75,000+ TPT searches</strong> to find
          the top keyword opportunities before the seasonal peak hits.
          Get the full ranked report — <strong>free, direct to your inbox.</strong>
        </p>
      </div>

      {/* MAIN 2-COL LAYOUT */}
      <div className="cdm-grid">

        {/* LEFT — Info Card */}
        <div className="cdm-info-card">
          <div className="cdm-section-label">What You're Getting</div>
          <h2 className="cdm-card-heading sora">Fresh data. Real opportunity.</h2>

          <p style={{fontSize: '15px', color: '#475569', lineHeight: 1.7, marginBottom: '24px'}}>
            We analysed <strong style={{color: '#0f172a'}}>75,000+ TPT searches</strong> on April 26th 2026 to find the best Cinco de Mayo keyword opportunities — ranked by our Golden Score formula before the seasonal peak hits.
          </p>

          <div className="cdm-perks-grid" style={{marginBottom: '24px'}}>
            <div className="cdm-perk"><span style={{color: '#16a34a', fontSize: '16px'}}>⭐</span> Golden Score Rankings</div>
            <div className="cdm-perk"><span style={{color: '#1a56db', fontSize: '16px'}}>📈</span> Popularity Data</div>
            <div className="cdm-perk"><span style={{color: '#f59e0b', fontSize: '16px'}}>🏆</span> Competition Numbers</div>
            <div className="cdm-perk"><span style={{color: '#8b5cf6', fontSize: '16px'}}>📅</span> Seasonal Timing Tips</div>
            <div className="cdm-perk"><span style={{color: '#ec4899', fontSize: '16px'}}>💎</span> Hidden Gem Keywords</div>
            <div className="cdm-perk"><span style={{color: '#06b6d4', fontSize: '16px'}}>🎁</span> $25 Amazon Draw Entry</div>
          </div>

          <div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px'}}>
            <div style={{fontSize: '13px', fontWeight: 700, color: '#14532d', marginBottom: '4px'}}>💡 The window is closing fast</div>
            <div style={{fontSize: '13px', color: '#15803d', lineHeight: 1.6}}>
              Cinco de Mayo is {daysAway}. TPT's algorithm rewards products with existing rank history when searches spike — publish now, not the week of.
            </div>
          </div>
        </div>

        {/* RIGHT — Sticky Form Card */}
        <div className="cdm-form-card">
          <div className="cdm-section-label">Free · No Card Needed</div>
          <h2 className="cdm-card-heading sora">Get the Cinco de Mayo Report</h2>

          {/* SUCCESS: NEW */}
          {submitState === 'success-new' && (
            <div style={{textAlign: 'center', padding: '16px 0'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>🌮</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>You're in!</div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px'}}>
                The Cinco de Mayo keyword report is on its way to <strong style={{color: '#0f172a'}}>{email}</strong>.<br/>
                You're also entered into the monthly $25 Amazon draw. 🎁
              </div>
              <div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px', fontSize: '13px', color: '#166534', marginBottom: '16px'}}>
                <strong>While you wait —</strong> try the full app free for 1 hour and search any keyword yourself.
              </div>
              <Link to="/app" className="cdm-submit-btn sora" style={{display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '50px', padding: '0 16px'}}>
                Try the App Free →
              </Link>
            </div>
          )}

          {/* SUCCESS: EXISTING */}
          {submitState === 'success-existing' && (
            <div style={{textAlign: 'center', padding: '16px 0'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>👋</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>We recognise you!</div>
              <div style={{fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '20px'}}>
                Looks like <strong style={{color: '#0f172a'}}>{email}</strong> has signed up with us before — don't worry, you're all set. 🌮<br/><br/>
                <strong>Check your inbox</strong> — the Cinco de Mayo keyword report is on its way right now!
              </div>
              <div style={{background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px', fontSize: '13px', color: '#166534', marginBottom: '16px'}}>
                Can't find it? Check your spam folder or search for <strong>"Mr Joel"</strong> in your inbox.
              </div>
              <Link to="/app" className="cdm-submit-btn sora" style={{display: 'block', textAlign: 'center', textDecoration: 'none', lineHeight: '50px', padding: '0 16px'}}>
                Go to the App →
              </Link>
            </div>
          )}

          {/* FORM */}
          {(submitState === 'idle' || submitState === 'loading' || submitState === 'error') && (
            <>
              <div style={{fontSize: '14px', color: '#64748b', marginBottom: '24px', lineHeight: 1.5}}>
                Get the full 20-keyword report — free, no card needed, unsubscribe any time.
              </div>
              <form onSubmit={handleFormSubmit}>
                <label className="cdm-field-label" htmlFor="cdm-email">Email Address *</label>
                <input className="cdm-field-input" type="email" id="cdm-email" placeholder="yourname@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={submitState === 'loading'} />
                <div className="cdm-gdpr-box">
                  <label className="cdm-gdpr-label">
                    <input type="checkbox" className="cdm-gdpr-checkbox" checked={gdprChecked} onChange={(e) => setGdprChecked(e.target.checked)} required disabled={submitState === 'loading'} />
                    <span className="cdm-gdpr-text">
                      <strong>Yes, send me the Cinco de Mayo keyword report.</strong><br />
                      Unsubscribe any time. We use Mailchimp. <a href="https://mailchimp.com/legal/terms" target="_blank" rel="noopener noreferrer">Privacy policy →</a>
                    </span>
                  </label>
                </div>
                {submitState === 'error' && (
                  <div style={{background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#dc2626'}}>{errorMsg}</div>
                )}
                <button type="submit" className="cdm-submit-btn" disabled={submitState === 'loading' || !gdprChecked} style={{opacity: submitState === 'loading' ? 0.7 : 1, cursor: submitState === 'loading' ? 'not-allowed' : 'pointer'}}>
                  {submitState === 'loading' ? 'Sending...' : 'Send Me the Report →'}
                </button>
                <div className="cdm-amazon-badge">🎁 <strong>Automatic Entry:</strong> Monthly $25 Amazon voucher draw</div>
                <div style={{textAlign: 'center', marginTop: '12px', fontSize: '11px', color: '#94a3b8'}}>🔒 No spam · Free forever · One email with your report</div>
              </form>
            </>
          )}
        </div>
      </div>

      {/* PREVIEW TABLE */}
      <div style={{maxWidth: '1080px', margin: '0 auto', padding: '0 24px 40px'}}>
        <div style={{background: 'white', border: '1px solid #e2e8f0', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.06)'}}>

          {/* Table Header */}
          <div style={{background: 'linear-gradient(135deg, #14532d, #16a34a)', padding: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px'}}>
            <div>
              <div style={{fontSize: '11px', fontWeight: 700, color: '#bbf7d0', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px'}}>🌮 Sneak Peek — Cinco de Mayo 2026 Report</div>
              <div className="sora" style={{fontWeight: 800, fontSize: '24px', color: 'white'}}>Top Cinco de Mayo keywords right now</div>
              <div style={{fontSize: '14px', color: '#bbf7d0', marginTop: '6px'}}>Analysed April 26th 2026 · Sign up above to get all 20 free</div>
            </div>
            <div style={{display: 'flex', gap: '12px'}}>
              <div style={{textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: '12px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '24px', color: '#fbbf24'}}>★ 100</div>
                <div style={{fontSize: '11px', color: '#bbf7d0', textTransform: 'uppercase', marginTop: '4px'}}>Top score</div>
              </div>
              <div style={{textAlign: 'center', background: 'rgba(255,255,255,0.1)', padding: '12px 20px', borderRadius: '12px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '24px', color: '#4ade80'}}>20</div>
                <div style={{fontSize: '11px', color: '#bbf7d0', textTransform: 'uppercase', marginTop: '4px'}}>Opportunities</div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div style={{background: '#f8fafc', padding: '16px 32px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap'}}>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em'}}>Score guide:</div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: '#f0fdf4', border: '1px solid #16a34a', color: '#15803d', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ 70+</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Gold Mine</span></div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: '#eff6ff', border: '1px solid #1a56db', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>★ 20–69</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Strong</span></div>
            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}><span className="sora" style={{background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '12px'}}>💎 GEM</span><span style={{fontSize: '13px', color: '#64748b', fontWeight: 500}}>Hidden Gem</span></div>
          </div>

          {/* Column Headers */}
          <div style={{display: 'grid', gridTemplateColumns: '32px 1fr 100px 90px 110px', gap: 0, padding: '14px 32px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0'}}>
            {['#', 'Keyword', 'Score', 'Popularity', 'Competition'].map((h, i) => (
              <div key={h} style={{fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', textAlign: i > 1 ? 'center' : 'left'}}>{h}</div>
            ))}
          </div>

          {/* Visible rows 1-5 */}
          <div>
            {visibleRows.map((kw) => (
              <div key={kw.rank} className="cdm-preview-row" style={{background: kw.rank % 2 === 0 ? '#fafafa' : 'white'}}>
                <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>{kw.rank}</div>
                <div>
                  <div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>{kw.kw}</div>
                  <div style={{fontSize: '12px', color: '#64748b', marginTop: '2px'}}>{kw.note}</div>
                </div>
                <div style={{textAlign: 'center'}}>{getScoreBadge(kw)}</div>
                <div className="cdm-hide-mobile" style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>{kw.pop}</div>
                <div className="cdm-hide-mobile" style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>{kw.comp}</div>
              </div>
            ))}

            {/* Blurred rows 6-8 */}
            <div style={{position: 'relative'}}>
              <div style={{filter: 'blur(5px)', pointerEvents: 'none', opacity: 0.6}}>
                {blurredRows.map((kw) => (
                  <div key={kw.rank} className="cdm-preview-row">
                    <div className="sora" style={{fontSize: '13px', fontWeight: 700, color: '#cbd5e1'}}>{kw.rank}</div>
                    <div>
                      <div style={{fontSize: '15px', fontWeight: 700, color: '#0f172a'}}>{kw.kw}</div>
                      <div style={{fontSize: '12px', color: '#64748b'}}>{kw.note}</div>
                    </div>
                    <div style={{textAlign: 'center'}}>{getScoreBadge(kw)}</div>
                    <div style={{fontSize: '14px', fontWeight: 600, color: '#475569', textAlign: 'right'}}>{kw.pop}</div>
                    <div style={{fontSize: '14px', color: '#94a3b8', textAlign: 'right'}}>{kw.comp}</div>
                  </div>
                ))}
              </div>

              {/* Blur CTA overlay */}
              <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(to bottom, rgba(248,250,252,0) 0%, rgba(248,250,252,0.9) 40%, rgba(248,250,252,1) 100%)'}}>
                <div style={{textAlign: 'center', padding: '30px', background: 'white', borderRadius: '20px', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0'}}>
                  <div className="sora" style={{fontWeight: 800, fontSize: '20px', color: '#0f172a', marginBottom: '8px'}}>+ 15 more keywords in the full report</div>
                  <div style={{fontSize: '14px', color: '#64748b', marginBottom: '20px'}}>Including 10 💎 hidden gems with almost zero competition</div>
                  <a href="#cdm-email" onClick={scrollToForm} className="sora" style={{background: 'linear-gradient(135deg, #16a34a, #14532d)', color: 'white', fontWeight: 800, fontSize: '15px', padding: '14px 32px', borderRadius: '12px', textDecoration: 'none', display: 'inline-block', boxShadow: '0 4px 16px rgba(22,163,74,0.3)'}}>
                    Get the Full Report Free →
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Capped score footnote */}
          <div style={{padding: '12px 32px', background: '#f8fafc', borderTop: '1px solid #e2e8f0'}}>
            <div style={{fontSize: '11px', color: '#94a3b8'}}>* Score capped at 100 for display. Actual Golden Score may be higher.</div>
          </div>
        </div>
      </div>

      {/* APP PROMO BANNER — compact */}
      <div className="cdm-app-promo">
        <div style={{background: 'linear-gradient(135deg, #1e3a8a, #1a56db)', borderRadius: '16px', padding: '20px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', boxShadow: '0 10px 40px rgba(26,86,219,0.2)'}}>
          <div style={{flex: 1, minWidth: '260px'}}>
            <div style={{fontSize: '11px', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px'}}>💡 Did you know?</div>
            <div className="sora" style={{fontWeight: 800, fontSize: '17px', color: 'white', lineHeight: 1.3}}>
              Our app lets you search all 75,000+ keywords — rank tracking, competitor analysis & listing optimisation in one place.
            </div>
          </div>
          <div style={{display: 'flex', gap: '8px', flexShrink: 0}}>
            {[['20', 'Keywords'], ['75k+', 'Analysed'], ['140+', '⭐ Reviews']].map(([num, label]) => (
              <div key={label} style={{textAlign: 'center', background: 'rgba(255,255,255,0.08)', borderRadius: '10px', padding: '10px 14px'}}>
                <div className="sora" style={{fontWeight: 900, fontSize: '20px', color: '#fbbf24', lineHeight: 1}}>{num}</div>
                <div style={{fontSize: '10px', color: '#93c5fd', marginTop: '3px', textTransform: 'uppercase', whiteSpace: 'nowrap'}}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{display: 'flex', gap: '10px', flexShrink: 0, flexWrap: 'wrap'}}>
            <Link to="/app" style={{background: 'white', color: '#1e40af', fontFamily: 'Sora, sans-serif', fontWeight: 800, fontSize: '13px', padding: '10px 20px', borderRadius: '12px', textDecoration: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'}}>🚀 Try Free →</Link>
            <a href="https://www.teacherspayteachers.com/Product/TPT-Seller-SEO-Listing-Optimizer-App-Create-Titles-Descriptions-SEO-Ranking-13645689" target="_blank" rel="noopener noreferrer" style={{background: 'transparent', border: '2px solid rgba(255,255,255,0.4)', color: 'white', fontFamily: 'Sora, sans-serif', fontWeight: 700, fontSize: '12px', padding: '10px 16px', borderRadius: '12px', textDecoration: 'none'}}>Get Lifetime Access</a>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer style={{background: 'white', borderTop: '1px solid #e2e8f0', textAlign: 'center', padding: '32px 24px', fontSize: '13px', color: '#64748b'}}>
        <p>
          <a href="https://mrjoelstptgenerator.com" style={{color: '#16a34a', textDecoration: 'none'}}>mrjoelstptgenerator.com</a> &nbsp;·&nbsp;
          <Link to="/app" style={{color: '#16a34a', textDecoration: 'none'}}>Try the App Free</Link> &nbsp;·&nbsp;
          <a href="https://www.teacherspayteachers.com/store/mr-joels-math-science-store" style={{color: '#16a34a', textDecoration: 'none'}}>My TPT Store</a>
        </p>
        <p style={{marginTop: '8px'}}>© 2026 Mr Joel's Seller Tools · Data analysed April 26th 2026 · Not affiliated with Teachers Pay Teachers.</p>
      </footer>

    </div>
  );
};

export default CincoDeMayo2026;